import { z } from 'zod';
import { RecurringStartAnchorSchema } from './opportunities';
// =====================================================================
// F42 — Plan de facturación de un contrato (oportunidad GANADA).
// El wizard crea en lote: hitos fijos + cuotas recurrentes (acotadas o no)
// + revenue share de partner. La previsión proyecta la facturación futura.
// =====================================================================
export const BillingPlanMilestoneInputSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(2000).nullable().optional(),
    /** Base imponible en céntimos. */
    amountCents: z.number().int().positive(),
    /** Fracción (0.21 = 21%). */
    vatRate: z.number().min(0).max(1).default(0.21),
    /** ISO date (YYYY-MM-DD) de facturación prevista. */
    dueDate: z.string().min(10),
});
export const BillingPlanRecurringInputSchema = z.object({
    /** Nombre interno de la cuota ("Cuota mensual servicio IA Busquets"). */
    name: z.string().min(1).max(300),
    /** Descripción de la línea de la factura ("Servicio de consultoría IA — cuota mensual"). */
    concept: z.string().min(1).max(500),
    /** Base imponible por cuota en céntimos. */
    amountCents: z.number().int().positive(),
    vatRate: z.number().min(0).max(1).default(0.21),
    irpfRate: z.number().min(0).max(1).default(0),
    periodicity: z.enum(['MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'YEARLY']),
    /** Día del mes de emisión (1-28). */
    dayOfMonth: z.number().int().min(1).max(28).default(1),
    /** ISO date desde la que arranca la primera cuota. Default: hoy. */
    startDate: z.string().min(10).nullable().optional(),
    /**
     * Ancla de arranque: 'DATE' (usa startDate) o una fase de ejecución. Con una
     * fase aún no alcanzada, la iguala se crea PAUSED y se activa al llegar a ella.
     */
    startAnchor: RecurringStartAnchorSchema.nullable().optional(),
    /** Nº total de cuotas (18 = "18 mensualidades"). null = indefinida. */
    maxOccurrences: z.number().int().positive().nullable().optional(),
    /** Fecha fin alternativa (no generar más allá). */
    endsAt: z.string().min(10).nullable().optional(),
});
export const CreateBillingPlanSchema = z.object({
    /** Partner con revenue share sobre la facturación del contrato (ej. Libertia). */
    revenueSharePartner: z.string().max(200).nullable().optional(),
    /** Fracción (0.20 = 20%). Sin tope 8%: esto NO es la comisión CAC del advisor. */
    revenueSharePct: z.number().min(0).max(1).nullable().optional(),
    milestones: z.array(BillingPlanMilestoneInputSchema).default([]),
    recurrings: z.array(BillingPlanRecurringInputSchema).default([]),
});
export const CreateBillingPlanResponseSchema = z.object({
    milestoneIds: z.array(z.string()),
    recurringIds: z.array(z.string()),
    revenueShareApplied: z.boolean(),
});
// ---------- Previsión ----------
export const BillingForecastItemSchema = z.object({
    source: z.enum(['MILESTONE', 'RECURRING']),
    label: z.string(),
    clientName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityTitle: z.string().nullable(),
    baseCents: z.number().int(),
    commissionCents: z.number().int(),
});
export const BillingForecastMonthSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    /** Base imponible proyectada (hitos pendientes + cuotas futuras). */
    projectedCents: z.number().int(),
    /** Comisiones revenue share proyectadas sobre esa base. */
    commissionCents: z.number().int(),
    /** projected − commission. */
    netCents: z.number().int(),
    /** Base imponible ya emitida en el mes (facturas no borrador/anuladas). */
    emittedCents: z.number().int(),
    /** Cobros del mes (importe total de pagos, IVA incluido). */
    collectedCents: z.number().int(),
    items: z.array(BillingForecastItemSchema),
});
export const BillingForecastResponseSchema = z.object({
    months: z.array(BillingForecastMonthSchema),
    /** Suma de projectedCents del rango. */
    totalProjectedCents: z.number().int(),
    totalCommissionCents: z.number().int(),
    totalNetCents: z.number().int(),
});
// =====================================================================
// «Pendientes» de Facturación: lo que YA toca facturar, sin borrador.
// Cuotas de las igualas que han llegado a su fecha + hitos de pago vencidos.
// Se emiten desde la propia pantalla; ya no se crea ningún borrador solo.
// =====================================================================
export const PendingBillingItemSchema = z.object({
    kind: z.enum(['RECURRING', 'MILESTONE']),
    /** Clave estable de la fila: `rec:<id>:<fecha>` o `ms:<id>`. */
    key: z.string(),
    /** Id de la iguala (RECURRING) o del hito (MILESTONE). */
    sourceId: z.string(),
    label: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityTitle: z.string().nullable(),
    /** Periodo que cubre la cuota («octubre 2026»); null en un hito. */
    period: z.string().nullable(),
    dueDate: z.string().datetime(),
    /** Días naturales de retraso respecto a la fecha prevista (0 = vence hoy). */
    daysLate: z.number().int().min(0),
    /**
     * true = AÚN NO HA VENCIDO: vence este mes o el siguiente (se enseña para verlo venir y poder adelantarla). Las que ya
     * vencieron o vencen hoy llevan false y son las que cuentan en la insignia de la pestaña y en el panel inicial.
     */
    upcoming: z.boolean().default(false),
    /** Días naturales que faltan para la fecha prevista (solo en las que aún no han vencido; 0 en las demás). */
    daysUntil: z.number().int().min(0).default(0),
    baseCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    /**
     * false: hay una cuota anterior de la misma iguala sin emitir. Las cuotas de una iguala se emiten POR ORDEN
     * (cada emisión avanza la iguala una cuota), así que solo la más antigua está disponible.
     */
    canEmit: z.boolean(),
});
export const PendingBillingResponseSchema = z.object({
    /** Lo vencido y, después, lo que vence este mes y el siguiente (marcado con `upcoming`), por fecha. */
    items: z.array(PendingBillingItemSchema),
    /** Lo YA VENCIDO (sin lo que `upcoming`): es lo que cuenta la insignia de la pestaña. */
    count: z.number().int(),
    totalBaseCents: z.number().int(),
    totalCents: z.number().int(),
    /** Lo que aún no ha vencido pero vence este mes o el siguiente. */
    upcomingCount: z.number().int().default(0),
    upcomingBaseCents: z.number().int().default(0),
    upcomingTotalCents: z.number().int().default(0),
});
//# sourceMappingURL=billing-plan.js.map